import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, getStoredToken, setStoredToken } from '../lib/api';
import type { AuthSession, LoginDestination, TenantNavLayout } from '../types';

export type LoginResult =
  | { session: AuthSession }
  | {
    chooseDestination: true;
    pickToken: string;
    destinations: LoginDestination[];
  };

type AuthContextValue = {
  session: AuthSession | null;
  loading: boolean;
  isGuest: boolean;
  isClient: boolean;
  isTenantStaff: boolean;
  isPlatform: boolean;
  /** Personal staff toolbar layout (server per-user). Null until loaded or for non-staff. */
  staffNavLayout: TenantNavLayout | null;
  setStaffNavLayout: (layout: TenantNavLayout | null) => void;
  reloadStaffNavLayout: () => Promise<void>;
  login: (email: string, password?: string, tenantSlug?: string | null) => Promise<LoginResult>;
  completeDestination: (pickToken: string, destination: string) => Promise<AuthSession>;
  /** @deprecated Use login() */
  loginTenant: (email: string, password?: string, tenantSlug?: string | null) => Promise<LoginResult>;
  /** @deprecated Use login() */
  loginPlatform: (email: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [staffNavLayout, setStaffNavLayout] = useState<TenantNavLayout | null>(null);

  const reloadStaffNavLayout = useCallback(async () => {
    if (!getStoredToken()) {
      setStaffNavLayout(null);
      return;
    }
    try {
      const { navLayout } = await api.getTenantCabinetSettings();
      setStaffNavLayout(navLayout ?? null);
    } catch {
      setStaffNavLayout(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!getStoredToken()) {
      setSession(null);
      setStaffNavLayout(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const next = await api.fetchSession();
      setSession(next);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (session?.type !== 'tenant_user') {
      setStaffNavLayout(null);
      return;
    }
    void reloadStaffNavLayout();
  }, [session?.type, session?.id, session?.tenantId, session?.platformGrant, reloadStaffNavLayout]);

  const login = useCallback(async (email: string, password?: string, tenantSlug?: string | null) => {
    const data = await api.login(email, password, tenantSlug);
    if ('chooseDestination' in data && data.chooseDestination) {
      return {
        chooseDestination: true as const,
        pickToken: data.pickToken,
        destinations: data.destinations,
      };
    }
    const next = (data as { session: AuthSession }).session;
    setSession(next);
    return { session: next };
  }, []);

  const completeDestination = useCallback(async (pickToken: string, destination: string) => {
    const { session: next } = await api.completeLoginDestination(pickToken, destination);
    setSession(next);
    return next;
  }, []);

  const loginTenant = login;
  const loginPlatform = useCallback(async (email: string, password: string) => login(email, password), [login]);

  const logout = useCallback(async () => {
    await api.logout();
    setSession(null);
    setStaffNavLayout(null);
    setStoredToken(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      loading,
      isGuest: !session,
      isClient: session?.type === 'client',
      isTenantStaff: session?.type === 'tenant_user',
      isPlatform: session?.type === 'platform',
      staffNavLayout,
      setStaffNavLayout,
      reloadStaffNavLayout,
      login,
      completeDestination,
      loginTenant,
      loginPlatform,
      logout,
      refresh,
    }),
    [
      session,
      loading,
      staffNavLayout,
      reloadStaffNavLayout,
      login,
      completeDestination,
      loginTenant,
      loginPlatform,
      logout,
      refresh,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
